import { SettlementRecord } from "./CaseModel.ts";

/**
 * Where a settlement landed in the rational range of its offer, and whether
 * there was such a range at all. Kept apart from the page so the one place
 * that decides both can be unit-tested.
 */

/** The range of an offer at its stage, from the party's side. */
type Range = Pick<Required<SettlementRecord>, "partyEv" | "opponentEv" | "mid">;

/**
 * True when the record has a range to place its settlement in. There is none
 * when the offer had no range at its stage, and none to measure by when the
 * opponent's expected value does not lie above the party's own: the two leave
 * no room to agree in, and a position between them would be undefined or read
 * the wrong way round.
 */
export function hasRange<R extends Partial<Range>>(
  record: R,
): record is R & Range {
  const { partyEv, opponentEv, mid } = record;
  return (
    partyEv !== undefined &&
    opponentEv !== undefined &&
    mid !== undefined &&
    opponentEv > partyEv
  );
}

/**
 * Where the settlement landed between the two expected values: 0 at the
 * party's own, 0.5 at the even split, 1 at the opponent's. Below 0 or above 1
 * for a settlement outside the range, without bound.
 */
export const captureOf = ({
  settledValue,
  partyEv,
  opponentEv,
}: Pick<SettlementRecord, "settledValue"> & Range): number =>
  (settledValue - partyEv) / (opponentEv - partyEv);
